import { ApplicationRef, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { IRootState } from '../../../app.config';
import writeXlsxFile, { Cell } from 'write-excel-file/browser';
import { getNumberColumnGenericConfig, getTimeColumnGenericConfig } from '../../../../shared/bryntum/Helper/MogoBryntumGridConfigHelper';
import { Store } from '@ngrx/store';
import { OrderSource } from '../../../../shared/orders/models/OrderSource';
import { BryntumComboComponent, BryntumDateRangeFieldComponent, BryntumGridComponent, BryntumGridModule, BryntumGridProps } from '@bryntum/grid-angular';
import { NgbCalendar, NgbDatepickerModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, take } from 'rxjs';
import { selectUserType } from '../../../../shared/app-configuration/app-configuration.reducer';
import { UserType } from '../../../../shared/app-configuration/models/UserType';
import { ColumnStore, GridColumnConfig, Menu, MenuItemConfig, ColumnConfig, StateProvider, DateHelper } from '@bryntum/grid';
import { CompleteOrderModalComponent } from '../../../../shared/modals/complete-order-modal/complete-order-modal.component';
import { OrderEyeViewModalComponent } from '../../../../shared/modals/order-eye-view-modal/order-eye-view-modal.component';
import { OrderService } from '../../../../shared/orders/order.service';
import dayjs from 'dayjs';
import { ProofOfDeliveryPdfService } from '../../../../shared/proof-of-delivery/proof-of-delivery-pdf.service';
import { BlobStorageService } from '../../../../shared/blob-storage/blob-storage.service';
import { HttpErrorResponse } from '@angular/common/http';
import { OrderPalletLabelModalComponent } from '../../../../shared/modals/order-pallet-label-modal/order-pallet-label-modal.component';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { bootstrapArrowsAngleContract, bootstrapArrowsAngleExpand } from '@ng-icons/bootstrap-icons';
import { OrderPreviewPodModalComponent } from '../../../../shared/modals/order-preview-pod-modal/order-preview-pod-modal.component';
import { BryntumGridStateService } from '../../../../shared/bryntum/bryntum-grid-state.service';
import { BryntumGridStateProvider } from '../../../../shared/bryntum/bryntum-grid-state-provider';
import { ToasterService } from '../../../../shared/toaster/toaster.service';
import { LoggingService } from '../../../../shared/logging/logging.service';

@Component({
  selector: 'app-order-view',
  standalone: true,
  imports: [NgbDatepickerModule, BryntumGridModule, NgIcon],
  templateUrl: './order-view.component.html',
  styleUrl: './order-view.component.scss',
  viewProviders: [
    provideIcons(
      {
        bootstrapArrowsAngleExpand,
        bootstrapArrowsAngleContract

      }
    )
  ]
})
export class OrderViewComponent implements OnInit, OnDestroy {
  calendar = inject(NgbCalendar);

  data: any[] = [];
  totalItems = 0;
  isInitializing = true;
  ngDestroyed$ = new Subject();
  orderSources: OrderSource[] = [];

  private _orderSourceGrid: BryntumGridComponent | null = null;
  @ViewChild('orderSourceGrid')
  set orderSourceGrid(value: BryntumGridComponent | null) {
    this._orderSourceGrid = value;
    this.updateTotalItemsFromStore();
  }
  get orderSourceGrid(): BryntumGridComponent | null {
    return this._orderSourceGrid;
  }

  @ViewChild('dateFilterInput')
  dateFilterInput: BryntumDateRangeFieldComponent | null = null;

  @ViewChild('deliveryDateFilterInput')
  deliveryDateFilterInput: BryntumComboComponent | null = null;

  userType: UserType | null = null;

  defaultDaysToFilter: number = 7;
  isLoadingOrders: boolean = false;

  gridStateId = 'orderSourceGrid';
  gridStateProvider: BryntumGridStateProvider = new BryntumGridStateProvider(this.bryntumGridStateService, 26, this.gridStateId);;

  constructor(private router: Router,
    private store$: Store<IRootState>,
    private modalService: NgbModal,
    private orderService: OrderService,
    private proofOfDeliveryPdfService: ProofOfDeliveryPdfService,
    private blobStorageService: BlobStorageService,
    private bryntumGridStateService: BryntumGridStateService,
    private readonly appRef: ApplicationRef,
    private toasterService: ToasterService,
    private readonly loggingService: LoggingService
  ) { }

  gridProps: BryntumGridProps | null = null;
  dateRangeFilterComboConfig: Partial<BryntumComboComponent> | any = {
    items: [
      'Requested Date',
      'Delivery Date',
    ],
    value: this.getFilterColumnState(),
    width: '10em',
    listeners: {
      change: (event: any) => {
        this.saveFilterColumnState(event.value);
        this.loadOrderList();
      },
    }
  }

  dateRangeConfig: Partial<BryntumDateRangeFieldComponent> | any = {
    format: 'DD/MM/YYYY',
    value: [dayjs().subtract(this.defaultDaysToFilter, 'days'), 'today'],
    listeners: {
      change: (event: any) => {
        this.deliveryDateFilterInput?.instance.mask("Loading...");
        this.saveFilterState(event.value[0], event.value[1]);
        if (this.orderSourceGrid) {
          this.loadOrderList();
        }
        this.deliveryDateFilterInput?.instance.unmask();
      },
      catchAll: (event: any) => {
        if (event.type === 'paint' && event.firstPaint) {
          const filterState = this.getFilterState();
          if (filterState) {
            this.dateRangeConfig.value = [dayjs(filterState.from), dayjs(filterState.to)];
          }
        }
      }
    }
  }

  resetFilters() {
    this.dateRangeConfig.value = [dayjs().subtract(this.defaultDaysToFilter, 'days').startOf('day'), 'today'];
    this.dateRangeFilterComboConfig.value = 'Delivery Date';
  }

  ngOnDestroy(): void {
    if (this.gridStateProvider) {
      this.gridStateProvider.destroy();
    }
  }

  ngOnInit() {
    this.isInitializing = true;
    this.data = [];

    this.gridStateProvider
    .init()
    .then(() => this.initialiseGrid());
  }

  isSubUser = (): boolean => this.userType === UserType.Subcontractor;
  isFreightUser = (): boolean => this.userType === UserType.FreightPayer;

  private initialiseGrid() {
    this.store$.select(selectUserType).subscribe((userType) => {
      this.userType = userType;
      let columns: (GridColumnConfig & { field: string, excelFormat?: string | ((value: number) => string) })[] = [
        {
          id: "order_open_pod",
          text: 'Open POD',
          field: 'openPod',
          editor: false,
          editable: false,
          hidden: false,
          align: 'center',
          width: '10em',
          flex: 1,
          htmlEncode: false,
          renderer: ({ value, isExport, record }: { value: string; isExport: boolean; record: any }) => {
            if (record && record.completedByDeviceDate && record.completedByDeviceDate instanceof Date && !isNaN(record.completedByDeviceDate.getTime())) {
              if (isExport) {
                return 'Yes';
              } else {
                return `<button type="button" class="btn btn-primary w-100">Open POD</button>`;
              }
            } else {
              if (isExport) {
                return 'No';
              } else {
                return ``;
              }
            }
          }
        },
        {
          id: "order_warehouse_status",
          text: 'Warehouse Status',
          field: 'informationStatusId',
          editor: false,
          editable: false,
          hidden: false,
          align: 'center',
          width: '10em',
          htmlEncode: false,
          groupRenderer: (render: any) => render.record.informationStatusId === 60 ? 'Confirmed' : 'Unconfirmed',
          renderer: ({ value, isExport, record }: { value: number; isExport: boolean; record: any }) => {
            if (isExport) {
              return record.informationStatusId === 60 ? 'Confirmed' : 'Unconfirmed';
            }
            const isChecked = record.informationStatusId === 60;
            return `<div class="informationStatusContainer"><div class="round"><input type="checkbox" disabled="disabled" class="isPickConfirmed" ${isChecked ? 'checked' : ''}><label for="checkbox"></label></input></div></div>`;
          },
          filterable: {
            filterField: {
              type: 'combo',
              items: [
                { text: 'Confirmed', value: 60 },
                { text: 'Unconfirmed', value: 1 }
              ]
            }
          }
        },
        {
          id: "order_requested_date", field: 'requestedDate', text: 'Requested Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy',
          renderer: (data: any) => {
            return data.record.requestedDate && data.record.requestedDate.toString() !== 'Invalid Date' ? dayjs(data.record.requestedDate).format('DD/MM/YYYY') : '';
          }
        },
        {
          id: "order_delivery_date", field: 'deliveryDate', text: 'Planned Delivery Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy',
          renderer: (data: any) => {
            return data.record.deliveryDate && data.record.deliveryDate.toString() !== 'Invalid Date' ? dayjs(data.record.deliveryDate).format('DD/MM/YYYY') : '';
          }
        },
        {
          id: "order_actual_delivery_date", field: 'completedByDeviceDate', text: 'Actual Delivery Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy',
          renderer: (data: any) => {
            return data.record.completedByDeviceDate && data.record.completedByDeviceDate.toString() !== 'Invalid Date' ? dayjs(data.record.completedByDeviceDate).format('DD/MM/YYYY') : '';
          }
        },
        {
          id: "order_created_on",
          field: 'createdOn',
          text: 'Created Date',
          autoWidth: 1,
          type: 'date', format: 'DD/MM/YYYY h:mm:ss A', excelFormat: 'dd/mm/yyyy h:mm:ss AM/PM',
          renderer: (data: any) => {
            return data.record.createdOn && data.record.createdOn.toString() !== 'Invalid Date' ? dayjs(data.record.createdOn).format('DD/MM/YYYY h:mm:ss A') : '';
          }
        },
        {
          id: "order_delivery_time", field: 'deliveryTime', text: 'Planned Delivery Time', autoWidth: 1, type: 'time', format: 'h:mm:ss A',
          ...getTimeColumnGenericConfig('h:mm:ss A', 'h:mm:ss AM/PM')
        },
        {
          id: "order_actual_delivery_time", field: 'completedByDeviceDate', text: 'Actual Delivery Time', autoWidth: 1, type: 'time', format: 'h:mm:ss A',
          ...getTimeColumnGenericConfig('h:mm:ss A', 'h:mm:ss AM/PM')
        },
        { id: "order_container_number", field: 'containerNumber', text: 'Container Number', autoWidth: 1 },
        { id: "order_delivery_name", field: 'deliveryName', text: 'Receiver', autoWidth: 1 },
        { id: "order_delivery_address", field: 'deliveryAddress', text: 'Receiver Address', autoWidth: 1 },
        { id: "order_manifest_id", field: 'manifestId', text: 'Manifest ID', autoWidth: 1 },
        { id: "order_number_order", field: 'orderNumber', text: 'Order Number', autoWidth: 1 },
        { id: "order_pick_up_detail_name", field: 'pickUpDetailName', text: 'Sender', autoWidth: 1 },
        { id: "order_site_contact", field: 'siteContact', text: 'Site Contact', autoWidth: 1 },
        { id: "order_site_contact_phone", field: 'siteContactPhone', text: 'Site Contact Phone', autoWidth: 1 },
        { id: "order_site_contact_email", field: 'siteContactEmail', text: 'Site Contact Email', autoWidth: 1 },

        {
          id: "order_pickup_window_open_date", field: 'pickupOpenDate', text: 'Pickup Open Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy'
        },
        { id: "order_pickup_window_open_time", field: 'pickupOpenTime', text: 'Pickup Open Time', autoWidth: 1, type: 'time', ...getTimeColumnGenericConfig('LT', 'h:mm AM/PM') },
        { id: "order_pickup_window_close_date", field: 'pickupCloseDate', text: 'Pickup Close Date', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy' },
        { id: "order_pickup_window_close_time", field: 'pickupCloseTime', text: 'Pickup Close Time', autoWidth: 1, type: 'time', ...getTimeColumnGenericConfig('LT', 'h:mm AM/PM') },

        { id: "order_customer_contact", field: 'customerContact', text: 'Customer Contact', autoWidth: 1 },
        { id: "order_customer_contact_phone", field: 'customerContactPhone', text: 'Customer Contact Phone', autoWidth: 1 },
        { id: "order_customer_contact_email", field: 'customerContactEmail', text: 'Customer Contact Email', autoWidth: 1 },

        { id: "order_product_code", field: 'productCode', text: 'Product Description', autoWidth: 1 },
        { id: "order_product_category", field: 'productCategory', text: 'Product Category', autoWidth: 1 },
        { id: "order_status", field: 'status', text: 'Status', autoWidth: 1 },
        { id: "order_units", field: 'units', text: 'Units', autoWidth: 1, type: 'number', ...getNumberColumnGenericConfig() },
        { id: "order_volume", field: 'volume', text: 'Volume', autoWidth: 1, type: 'number', ...getNumberColumnGenericConfig() },
        { id: "order_weight", field: 'weight', text: 'Weight', autoWidth: 1, type: 'number', ...getNumberColumnGenericConfig() },
        { id: "order_pallet", field: 'pallet', text: 'Pallet', autoWidth: 1, type: 'number', ...getNumberColumnGenericConfig() },
        { id: "order_lifts", field: 'lifts', text: 'Lift', autoWidth: 1, type: 'number', ...getNumberColumnGenericConfig() },
        { id: "order_consignment_note", field: 'consignmentNote', text: 'Consignment Note', autoWidth: 1 },
        { id: "order_customer_reference", field: 'customerReference', text: 'Customer Ref', autoWidth: 1 },
        { id: "order_verified_address", field: 'verifiedAddress', text: 'Sender Address', autoWidth: 1 },
        {
            id: "order_completed_by_device_date", field: 'completedByDeviceDate', text: 'Completed', autoWidth: 1, type: 'date', format: 'DD/MM/YYYY', excelFormat: 'dd/mm/yyyy',
            renderer: (data: any) => {
                return data.record.completedByDeviceDate && data.record.completedByDeviceDate.toString() !== 'Invalid Date' ? dayjs(data.record.completedByDeviceDate).format('DD/MM/YYYY') : '';
            }
        },
        { id: 'temp', text: 'Temp (°C)', field: 'temperature', autoWidth: 1, type: 'number', ...getNumberColumnGenericConfig() },
        { id: 'temp_type', text: 'Temp Type', field: 'temperatureType', autoWidth: 1 },
      ];

      if (this.userType === UserType.Subcontractor) {
        columns = [
          ...columns,
          { id: "order_carrier_name", field: 'carrierName', text: 'Carrier', autoWidth: 1 },
          {
            id: "order_pod_available",
            field: 'podAvailable',
            text: 'POD Available',
            autoWidth: 1,
            renderer: (data: any) => {
              return data.record.podAvailable ? 'Yes' : 'No';
            },
          },
        ];
      } else {
        columns = [
          ...columns,
          { id: "order_freight_payer_name", field: 'freightPayerName', text: 'Freight Payer', autoWidth: 1 },
        ];
      }

      // Date and time columns can share a field, so each displayed column needs its own format.
      const excelFormats = new Map(columns.map(column => [column.text, column.excelFormat]));

      this.gridProps = {
        columns: [
          {
            id: 'order_action',
            width: '10em',
            text: 'Action',
            field: 'action',
            hidden: false,
            align: 'center',
            type: 'widget',
            hideable: false,
            draggable: false,
            htmlEncode: false,
            filterable: false,
            headerRenderer: ({ record }: any) => {
              return `<label>Action</label><br><button class="clearfilter btn-xs btn btn-primary">Clear Filters</button> `;
            },
            widgets: [
              {
                type: 'button',
                text: 'Options',
                menuIcon: undefined,

                onAction: ({ source: btn }: any) => {
                  let menuItems: Partial<MenuItemConfig>[] = [
                    {
                      text: 'View Order',
                    },
                    {
                      text: 'Edit Order',
                      hidden: !btn.cellInfo.record.isEditable || this.isSubUser()
                    },
                    {
                      text: 'Print Consignment',
                    },
                    {
                      text: 'Print Pallet Label',
                    },
                  ];

                  if (this.userType === UserType.Subcontractor
                    && btn.cellInfo.record.completedByDeviceDate.toString() === 'Invalid Date') {
                    menuItems.push({
                      text: 'Complete Order',
                    });
                  } else if (btn.cellInfo.record.completedByDeviceDate.toString() !== 'Invalid Date') {
                    menuItems.push(
                      {
                        text: 'Print POD',
                      });
                  }

                  if (menuItems.length > 0) {
                    const menu = (btn.menu || (btn.menu = new Menu({
                      forElement: btn.element,
                      items: menuItems,
                      onItem: ({ item }) => {
                        if (item.text === 'View Order') {
                          this.openOrderEyeView(btn.cellInfo.record.orderId);
                        } else if (item.text === 'Edit Order') {
                          this.editOrder(btn.cellInfo.record.orderId);
                        } else if (item.text === 'Complete Order') {
                          this.openCompleteOrderModel(btn.cellInfo.record.orderId, btn.cellInfo.record.createdOn);
                        } else if (item.text === 'Print POD') {
                          this.printOrderPodPdf(btn.cellInfo.record.orderId);
                        } else if (item.text === 'Print Consignment') {
                          this.printOrderConsignmentPdf(btn.cellInfo.record.orderId);
                        } else if (item.text === 'Print Pallet Label') {
                          this.openOrderPalletLabelModal(btn.cellInfo.record);
                        }
                      }
                    }))) as Menu;
                    menu.show();
                  }
                }
              }
            ],
          },
          ...columns
        ],
        stateful: true,
        minHeight: '85vh',
        filterBarFeature: true,
        cellEditFeature: false,
        excelExporterFeature: {
          zipcelx: ({ filename, rows, columns: exportedColumns }: { filename: string, rows: { value: string | number | Date | null | undefined }[][], columns: { field: string, value: string }[] }) => {
            const data = rows.map(row => row.map(({ value }, index): Cell => {
              if (value === null || value === undefined || value === '') {
                return null;
              }
              const format = excelFormats.get(exportedColumns[index].value);
              if (value instanceof Date) {
                return Number.isNaN(value.getTime()) ? null : { value, type: Date, format: format as string };
              }
              if (typeof value === 'number') {
                return { value, type: Number, format: typeof format === 'function' ? format(value) : format };
              }
              return { value, type: String };
            }));
            return writeXlsxFile([
              exportedColumns.map(column => ({ value: column.value, type: String, fontWeight: 'bold' } as Cell)),
              ...data
            ]).toFile(`${filename}.xlsx`);
          }
        },
        store: {
          listeners: {
            change: () => this.updateTotalItemsFromStore(),
            filter: () => this.updateTotalItemsFromStore(),
          }
        },
        listeners: {
          headerClick: (event: any) => this.headerClick(event),
        },
      };

      this.isInitializing = false;

      this.appRef.tick();

      if (this.orderSourceGrid) {
        this.loadOrderList();
      }
    });
  }

  onCellClick({ record, column }: { record: any, column: ColumnConfig }) {
    if (column.id === 'order_open_pod' && record.completedByDeviceDate && record.completedByDeviceDate instanceof Date && !isNaN(record.completedByDeviceDate.getTime())) {
      this.openOrderPreviewPodModal(record.orderId, record.status);
    }

    if (column.id === 'order_warehouse_status') {
      this.togglePickConfirmedDate(record);
    }
  }

  togglePickConfirmedDate(record: any) {
    const currentStatus = record.informationStatusId === 60;
    const newStatus = !currentStatus;

    this.orderService.updatePickConfirmedDate(record.orderId, newStatus).pipe(take(1)).subscribe({
      next: (result: boolean) => {
        if (result) {
          record.informationStatusId = newStatus ? 60 : 1;
          this.toasterService.showDefault('Warehouse status updated successfully');
        } else {
          this.toasterService.showDanger('Failed to update warehouse status');
        }
      },
      error: (err: HttpErrorResponse) => {
        console.error('Error updating pick confirmed date:', err.message);
        this.toasterService.showDanger('Failed to update warehouse status');
      }
    });
  }

  headerClick(event: any) {
    if (event && event.domEvent && event.domEvent.target && event.domEvent.target.classList.contains('clearfilter')) {
      if (this.orderSourceGrid && this.orderSourceGrid.instance) {
        (this.orderSourceGrid.instance.store as any).clearFilters();
      }
    }
  }

  exportToCsv() {
    if (!this.orderSourceGrid || !this.orderSourceGrid.instance) {
      return;
    }

    const grid = this.orderSourceGrid.instance;
    const columns = (grid.columns as ColumnStore).visibleColumns.filter(column =>
      column.get('exportable') !== false && column.field && column.field !== 'action');
    const date = dayjs().format('YYYY-MM-DD-HH-mm');
    const exportConfig = {
      filename: `trifecta-orders-${date}`,
      dateFormat: '',
      exporterConfig: { columns }
    };
    return grid.features.excelExporter.export(exportConfig);
  }

  toggleAllGroupings(action: 'expand' | 'collapse') {
    if (this.orderSourceGrid && this.orderSourceGrid.instance) {
      if (action === 'collapse') {
        this.orderSourceGrid.instance.collapseAll();
      }

      if (action === 'expand') {
        this.orderSourceGrid.instance.expandAll();
      }
    }
  }

  getFilterState() {
    const result = localStorage.getItem('orderViewFilterState');
    if (result) {
      return JSON.parse(result);
    }
  }

  saveFilterState(from: Date, to: Date) {
    localStorage.setItem('orderViewFilterState', JSON.stringify({
      from: from,
      to: to
    }));
  }

  saveFilterColumnState(column: string) {
    localStorage.setItem('orderViewFilterColumnState', JSON.stringify(column));
  }

  getFilterColumnState() {
    const result = localStorage.getItem('orderViewFilterColumnState');
    if (result) {
      return JSON.parse(result);
    }
  }

  getNormalizeDateRange() {
    if (this.dateFilterInput === null) {
      return {
        from: new Date(dayjs().subtract(this.defaultDaysToFilter, 'days').format()),
        to: new Date(dayjs().format())
      };
    } else {

      const fromDayJs = dayjs(this.dateFilterInput.instance.value[0]);
      const toDayJs = dayjs(this.dateFilterInput.instance.value[1]);

      return {
        from: new Date(fromDayJs.toString()),
        to: new Date(toDayJs.toString())
      };
    }
  }

  loadOrderList() {
    if (this.isLoadingOrders) {
      this.orderSourceGrid?.instance.mask('Loading...');
      return;
    }

    this.isLoadingOrders = true;
    this.orderSourceGrid?.instance.mask('Loading...');
    const dateRange = this.getNormalizeDateRange();
    const filterColumn = this.getFilterColumnState();
    this.orderService.getAllOrderSourcesList(dateRange.from, dateRange.to, filterColumn).pipe(take(1)).subscribe({
      next: (orderSources) => {
        this.data = [];
        orderSources.map((orderSource: any) => {
          this.data?.push({
            ...orderSource,
            requestedDate: new Date(orderSource.requestedDate),
            deliveryDate: new Date(orderSource.deliveryDate),
            deliveryTime: new Date(orderSource.deliveryTime),
            createdOn: new Date(orderSource.createdOn),
            completedByDeviceDate: new Date(orderSource.completedByDeviceDate),
            pickupOpenDate: DateHelper.parse(orderSource.pickupOpenDate, "DD/MM/YYYY"),
            pickupCloseDate: DateHelper.parse(orderSource.pickupCloseDate, "DD/MM/YYYY"),
            pickupCloseTime: this.getDateFromTimeInput(orderSource.pickupCloseTime),
            pickupOpenTime: this.getDateFromTimeInput(orderSource.pickupOpenTime),
          });
        });
        this.totalItems = this.data.length;
        queueMicrotask(() => this.updateTotalItemsFromStore());

        if (this.orderSourceGrid?.instance && !this.orderSourceGrid?.instance.isDestroyed) {
          this.orderSourceGrid?.instance.unmask();
        }
        this.isLoadingOrders = false;
      },
      error: (err) => {
        this.orderSourceGrid?.instance.unmask();
        this.isLoadingOrders = false;
        if (err.status === 401) {
          this.toasterService.showDanger("Refresh token failed. Please log out and log back in.");
        } else {
          this.toasterService.showDanger("Failed to load jobs!", true);
        }
      },
    });
  }

  getDateFromTimeInput(timeInput: string): Date | null {
    if (!timeInput) {
      return null;
    }

    const timeParts = timeInput.split(':');
    if (timeParts.length !== 3) {
      return null;
    }
    const now = dayjs();
    return new Date(now.hour(parseInt(timeParts[0], 10)).minute(parseInt(timeParts[1], 10)).second(parseInt(timeParts[2], 10)).toString());
  }

  private updateTotalItemsFromStore() {
    const gridStore = this.orderSourceGrid?.instance?.store as any;
    if (!gridStore) {
      this.totalItems = this.data.length;
      return;
    }

    const allRecords: any[] = gridStore.allRecords as any[];
    if (!allRecords || allRecords.length === 0) {
      this.totalItems = 0;
      return;
    }

    const filterFn = gridStore.filtersFunction;
    const hasActiveFilterFn = typeof filterFn === 'function';

    let count = 0;
    for (let i = 0; i < allRecords.length; i++) {
      const record = allRecords[i];
      if (record && !record.isSpecialRow && (!hasActiveFilterFn || filterFn(record))) {
        count++;
      }
    }

    this.totalItems = count;
  }

  navigateToOrder(orderKey: string) {
    this.router.navigate([`app/order/${orderKey}/edit`]);
  }

  createNewOrder() {
    this.router.navigate([`app/order/create`]);
  }

  openCompleteOrderModel(orderId: number, createdOn: Date) {
    const modalRef = this.modalService.open(CompleteOrderModalComponent);
    modalRef.componentInstance.orderId = orderId;
    modalRef.componentInstance.defaultStartDateTime = dayjs();
  }

  editOrder(orderId: string) {
    this.router.navigate([`app/order/${orderId}/edit`]);
  }

  openOrderEyeView(orderId: number) {
    const modalRef = this.modalService.open(OrderEyeViewModalComponent, { size: 'xl' });
    modalRef.componentInstance.orderId = orderId;
  }

  openOrderPalletLabelModal(order: any) {
    const modalRef = this.modalService.open(OrderPalletLabelModalComponent);
    modalRef.componentInstance.order = order;
  }

  openOrderPreviewPodModal(orderId: number, deliveryStatus: string) {
    const modalRef = this.modalService.open(OrderPreviewPodModalComponent, { size: 'xl' });
    modalRef.componentInstance.orderId = orderId;
    modalRef.componentInstance.deliveryStatus = deliveryStatus;
  }

  printOrderPodPdf(orderId: number) {
    this.proofOfDeliveryPdfService.printOrderPodPdf(orderId).subscribe({
      next: (response) => {
        if (response) {
          this.blobStorageService.downloadFile(response.fileBytes, response.fileName);
        }
      },
      error: (response: HttpErrorResponse) => {
        console.error(response.message, 'An error occurred:');
      }
    });
  }

  printOrderConsignmentPdf(orderId: number) {
    this.proofOfDeliveryPdfService.printOrderConsignmentPdf(orderId).subscribe({
      next: (response) => {
        if (response) {
          this.blobStorageService.downloadFile(response.fileBytes, response.fileName);
        }
      },
      error: (response: HttpErrorResponse) => {
        console.error(response.message, 'An error occurred:');
      }
    });
  }
}
